import type {
  TweetReply,
  EnhancedSuspiciousActivityReport,
  BotAccount,
  TokenShillNetwork,
  SuspiciousTokenAlert,
  ContentQualityMetrics
} from '@podx/core/types';
import { config } from '@podx/core/config';
import { logger } from '@podx/core/logger';
import { BotTracker } from '../analyzers/BotTracker.js';
import { PumpDumpDetector } from '../analyzers/PumpDumpDetector.js';
import { SuspiciousActivityMonitor } from '../analyzers/SuspiciousActivityMonitor.js';
import { GenericContentDetector } from '../analyzers/GenericContentDetector.js';

/**
 * TokenShillMonitorService - Comprehensive token shilling and pump/dump monitoring
 * 
 * This service integrates bot tracking, pump/dump detection, and alert generation
 * to provide comprehensive monitoring of token promotion campaigns and market manipulation
 */
export class TokenShillMonitorService {
  private botTracker: BotTracker;
  private pumpDumpDetector: PumpDumpDetector;
  private suspiciousActivityMonitor: SuspiciousActivityMonitor;
  private genericDetector: GenericContentDetector;

  // Alert management
  private alertHistory = new Map<string, SuspiciousTokenAlert[]>();
  private criticalTokens = new Set<string>();
  private monitoredNetworks = new Map<string, Date>(); // token -> last update

  constructor() {
    this.botTracker = new BotTracker();
    this.pumpDumpDetector = new PumpDumpDetector(this.botTracker);
    this.suspiciousActivityMonitor = new SuspiciousActivityMonitor();
    this.genericDetector = new GenericContentDetector();

    logger.info('TokenShillMonitorService initialized', {
      operation: 'token_shill_monitor_init'
    });
  }

  /**
   * Main analysis method - processes replies and generates comprehensive report
   */
  async analyzeTokenShilling(replies: TweetReply[]): Promise<EnhancedSuspiciousActivityReport> {
    logger.info('Starting comprehensive token shilling analysis', {
      operation: 'analyze_token_shilling',
      replyCount: replies.length
    });

    if (replies.length === 0) {
      return this.generateEmptyReport();
    }

    // Step 1: Basic suspicious activity analysis
    const baseReport = this.suspiciousActivityMonitor.generateReport(replies);

    // Step 2: Enhanced content quality analysis for bot detection
    const qualityAnalysis = await this.analyzeContentQuality(replies);

    // Step 3: Track bot accounts and their shilling activity
    const botAccounts = await this.trackBotAccounts(replies, qualityAnalysis);

    // Step 4: Detect pump/dump patterns
    const pumpDumpAnalysis = this.pumpDumpDetector.analyzeTokenShillingPatterns(replies);

    // Step 5: Generate enhanced report
    const enhancedReport = this.generateEnhancedReport(
      baseReport,
      botAccounts,
      pumpDumpAnalysis
    );

    // Step 6: Update monitoring state
    this.updateMonitoringState(enhancedReport);

    logger.info('Token shilling analysis complete', {
      operation: 'token_shilling_complete',
      botAccountsTracked: botAccounts.length,
      suspiciousTokens: enhancedReport.tokenShilling.suspiciousTokens.length,
      activeAlerts: enhancedReport.tokenShilling.alerts.length,
      criticalAlerts: enhancedReport.tokenShilling.alerts.filter(a => a.severity === 'critical').length
    });

    return enhancedReport;
  }

  private async analyzeContentQuality(replies: TweetReply[]): Promise<Array<{reply: TweetReply; quality: ContentQualityMetrics}>> {
    const analysis: Array<{reply: TweetReply; quality: ContentQualityMetrics}> = [];

    if (!config.analysis.enableReplyAnalysis) {
      // Return default quality metrics if analysis is disabled
      return replies.map(reply => ({
        reply,
        quality: {
          genericityScore: 0,
          spamProbability: 0,
          manipulationScore: 0,
          originalityScore: 1,
          informationDensity: 0.5,
          credibilityMarkers: [],
          speculationLevel: 0,
          educationalValue: 0
        }
      }));
    }

    for (const reply of replies) {
      const quality = this.genericDetector.analyzeContentQuality(reply);
      analysis.push({ reply, quality });
    }

    return analysis;
  }

  private async trackBotAccounts(
    replies: TweetReply[], 
    qualityAnalysis: Array<{reply: TweetReply; quality: ContentQualityMetrics}>
  ): Promise<BotAccount[]> {
    if (!config.analysis.enableBotDetection) {
      return [];
    }

    const userReplies = new Map<string, TweetReply[]>();
    const userQualities = new Map<string, ContentQualityMetrics[]>();

    // Group replies and quality metrics by user
    for (const {reply, quality} of qualityAnalysis) {
      const username = reply.username;
      
      if (!userReplies.has(username)) {
        userReplies.set(username, []);
        userQualities.set(username, []);
      }
      
      userReplies.get(username)!.push(reply);
      userQualities.get(username)!.push(quality);
    }

    const trackedBots: BotAccount[] = [];

    // Analyze each user for bot behavior
    for (const [username, userReplyList] of userReplies) {
      const qualities = userQualities.get(username)!;
      
      // Calculate bot and shill scores
      const botScore = this.calculateBotScore(qualities, userReplyList);
      const shillScore = this.calculateShillScore(qualities, userReplyList);

      // Only track if above suspicion threshold
      if (botScore > 0.3 || shillScore > 0.4) {
        const botAccount = this.botTracker.trackBotAccount(
          username,
          userReplyList,
          qualities,
          botScore,
          shillScore
        );
        trackedBots.push(botAccount);
      }
    }

    return trackedBots;
  }

  private calculateBotScore(qualities: ContentQualityMetrics[], replies: TweetReply[]): number {
    if (qualities.length === 0) return 0;

    const avgGenericityScore = qualities.reduce((sum, q) => sum + q.genericityScore, 0) / qualities.length;
    const avgSpamProbability = qualities.reduce((sum, q) => sum + q.spamProbability, 0) / qualities.length;
    const avgOriginalityScore = qualities.reduce((sum, q) => sum + q.originalityScore, 0) / qualities.length;

    let botScore = 0;

    // High genericity indicates bot behavior
    botScore += avgGenericityScore * 0.4;

    // High spam probability
    botScore += avgSpamProbability * 0.3;

    // Low originality
    botScore += (1 - avgOriginalityScore) * 0.2;

    // High frequency posting (if available)
    if (replies.length > 5) {
      botScore += 0.1;
    }

    return Math.min(1, botScore);
  }

  private calculateShillScore(qualities: ContentQualityMetrics[], replies: TweetReply[]): number {
    if (qualities.length === 0) return 0;

    const avgManipulationScore = qualities.reduce((sum, q) => sum + q.manipulationScore, 0) / qualities.length;
    const avgSpeculationLevel = qualities.reduce((sum, q) => sum + q.speculationLevel, 0) / qualities.length;

    let shillScore = 0;

    // High manipulation indicates shilling
    shillScore += avgManipulationScore * 0.4;

    // High speculation
    shillScore += avgSpeculationLevel * 0.2;

    // Token mentions frequency
    const tokenMentions = replies.filter(r => /\$[A-Z]{2,10}/.test(r.text)).length;
    const tokenMentionRatio = tokenMentions / Math.max(replies.length, 1);
    shillScore += tokenMentionRatio * 0.3;

    // Urgency language
    const urgentReplies = replies.filter(r => 
      /(?:now|asap|quick|fast|last\s+chance|limited\s+time)/i.test(r.text)
    ).length;
    const urgencyRatio = urgentReplies / Math.max(replies.length, 1);
    shillScore += urgencyRatio * 0.1;

    return Math.min(1, shillScore);
  }

  private generateEnhancedReport(
    baseReport: any,
    botAccounts: BotAccount[],
    pumpDumpAnalysis: any
  ): EnhancedSuspiciousActivityReport {
    const enhancedReport: EnhancedSuspiciousActivityReport = {
      ...baseReport,
      tokenShilling: {
        suspiciousTokens: pumpDumpAnalysis.suspiciousNetworks,
        activeShillCampaigns: pumpDumpAnalysis.suspiciousNetworks.filter(
          (network: TokenShillNetwork) => network.campaignType === 'pump_and_dump'
        ).length,
        botAccountsTracked: botAccounts,
        alerts: pumpDumpAnalysis.activeAlerts
      },
      pumpDumpAnalysis: {
        activeCampaigns: pumpDumpAnalysis.suspiciousNetworks.filter(
          (network: TokenShillNetwork) => ['pump_and_dump', 'rugpull_preparation'].includes(network.campaignType)
        ),
        riskTokens: pumpDumpAnalysis.riskTokens,
        coordinatedNetworks: pumpDumpAnalysis.suspiciousNetworks.length,
        timeToAction: this.generateTimeToActionAlerts(pumpDumpAnalysis.activeAlerts)
      }
    };

    return enhancedReport;
  }

  private generateTimeToActionAlerts(alerts: SuspiciousTokenAlert[]): string[] {
    const actionAlerts: string[] = [];

    for (const alert of alerts) {
      if (alert.severity === 'critical') {
        actionAlerts.push(`URGENT: ${alert.tokenSymbol} - ${alert.riskAssessment.timeToLikelyDump}`);
      } else if (alert.severity === 'high' && alert.alertType === 'dump_incoming') {
        actionAlerts.push(`HIGH RISK: ${alert.tokenSymbol} - Dump likely in ${alert.riskAssessment.timeToLikelyDump}`);
      }
    }

    return actionAlerts;
  }

  private updateMonitoringState(report: EnhancedSuspiciousActivityReport): void {
    const now = new Date();

    // Update alert history
    for (const alert of report.tokenShilling.alerts) {
      if (!this.alertHistory.has(alert.tokenSymbol)) {
        this.alertHistory.set(alert.tokenSymbol, []);
      }
      this.alertHistory.get(alert.tokenSymbol)!.push(alert);

      // Mark critical tokens
      if (alert.severity === 'critical') {
        this.criticalTokens.add(alert.tokenSymbol);
      }
    }

    // Update monitored networks
    for (const network of report.tokenShilling.suspiciousTokens) {
      this.monitoredNetworks.set(network.tokenSymbol, now);
    }

    // Cleanup old data
    this.cleanupOldData();
  }

  private cleanupOldData(): void {
    const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // 7 days

    // Cleanup alert history
    for (const [token, alerts] of this.alertHistory) {
      const recentAlerts = alerts.filter(alert => alert.detectedAt > cutoff);
      if (recentAlerts.length === 0) {
        this.alertHistory.delete(token);
      } else {
        this.alertHistory.set(token, recentAlerts);
      }
    }

    // Cleanup monitored networks
    for (const [token, lastUpdate] of this.monitoredNetworks) {
      if (lastUpdate < cutoff) {
        this.monitoredNetworks.delete(token);
        this.criticalTokens.delete(token);
      }
    }

    // Cleanup bot tracker data
    this.botTracker.purgeOldBotData(7);

    // Cleanup pump/dump detector alerts
    this.pumpDumpDetector.clearExpiredAlerts(24);
  }

  private generateEmptyReport(): EnhancedSuspiciousActivityReport {
    const baseReport = this.suspiciousActivityMonitor.generateReport([]);
    return {
      ...baseReport,
      tokenShilling: {
        suspiciousTokens: [],
        activeShillCampaigns: 0,
        botAccountsTracked: [],
        alerts: []
      },
      pumpDumpAnalysis: {
        activeCampaigns: [],
        riskTokens: [],
        coordinatedNetworks: 0,
        timeToAction: []
      }
    };
  }

  // Public API methods

  /**
   * Get current critical token alerts
   */
  getCriticalAlerts(): SuspiciousTokenAlert[] {
    return this.pumpDumpDetector.getActiveAlerts()
      .filter(alert => alert.severity === 'critical');
  }

  /**
   * Get high-risk tokens with immediate action required
   */
  getHighRiskTokens(): Array<{
    token: string;
    riskLevel: number;
    timeToAction: string;
    recommendations: string[];
  }> {
    const highRiskNetworks = this.pumpDumpDetector.getHighRiskTokens();
    
    return highRiskNetworks.map(network => {
      const alert = this.pumpDumpDetector.getActiveAlerts()
        .find(a => a.tokenSymbol === network.tokenSymbol);
      
      return {
        token: network.tokenSymbol,
        riskLevel: network.suspicionLevel,
        timeToAction: alert?.riskAssessment.timeToLikelyDump || 'unknown',
        recommendations: alert?.recommendations || [
          'Monitor for pump/dump activity',
          'Verify token fundamentals',
          'Check for bot account involvement'
        ]
      };
    }).slice(0, 10); // Top 10 highest risk
  }

  /**
   * Get bot account summary for a specific token
   */
  getTokenBotAnalysis(tokenSymbol: string): {
    botAccounts: BotAccount[];
    totalShillers: number;
    botRatio: number;
    averageBotScore: number;
    shillPatterns: string[];
  } {
    const botAccounts = this.botTracker.getTokenShillers(tokenSymbol);
    const totalShillers = this.pumpDumpDetector.getTokenNetwork(tokenSymbol)?.networkAccounts.length || 0;
    
    const botRatio = totalShillers > 0 ? botAccounts.length / totalShillers : 0;
    const averageBotScore = botAccounts.length > 0 ? 
      botAccounts.reduce((sum, bot) => sum + bot.botScore, 0) / botAccounts.length : 0;

    // Extract common patterns
    const allPatterns = botAccounts.flatMap(bot => bot.behaviorPatterns);
    const patternCounts = new Map<string, number>();
    for (const pattern of allPatterns) {
      patternCounts.set(pattern, (patternCounts.get(pattern) || 0) + 1);
    }
    
    const shillPatterns = Array.from(patternCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([pattern]) => pattern);

    return {
      botAccounts: botAccounts.slice(0, 20), // Top 20 most suspicious
      totalShillers,
      botRatio: Math.round(botRatio * 100) / 100,
      averageBotScore: Math.round(averageBotScore * 100) / 100,
      shillPatterns
    };
  }

  /**
   * Get comprehensive monitoring statistics
   */
  getMonitoringStatistics(): {
    botTracker: any;
    pumpDumpDetector: any;
    alertSummary: {
      totalAlerts: number;
      criticalAlerts: number;
      tokensMonitored: number;
      activeInvestigations: number;
    };
    topRisks: Array<{
      token: string;
      riskLevel: number;
      alertType: string;
    }>;
  } {
    const botStats = this.botTracker.getBotStatistics();
    const networkStats = this.pumpDumpDetector.getNetworkStatistics();
    const activeAlerts = this.pumpDumpDetector.getActiveAlerts();
    
    const criticalAlerts = activeAlerts.filter(a => a.severity === 'critical').length;
    const topRisks = activeAlerts
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 5)
      .map(alert => ({
        token: alert.tokenSymbol,
        riskLevel: alert.confidence,
        alertType: alert.alertType
      }));

    return {
      botTracker: botStats,
      pumpDumpDetector: networkStats,
      alertSummary: {
        totalAlerts: activeAlerts.length,
        criticalAlerts,
        tokensMonitored: this.monitoredNetworks.size,
        activeInvestigations: networkStats.highRiskNetworks
      },
      topRisks
    };
  }

  /**
   * Check if a specific token is flagged as suspicious
   */
  isTokenSuspicious(tokenSymbol: string): {
    isSuspicious: boolean;
    riskLevel: number;
    reasons: string[];
    botCount: number;
    lastActivity: Date | null;
  } {
    const network = this.pumpDumpDetector.getTokenNetwork(tokenSymbol);
    const botAccounts = this.botTracker.getTokenShillers(tokenSymbol);
    const activeAlert = this.pumpDumpDetector.getActiveAlerts()
      .find(alert => alert.tokenSymbol === tokenSymbol);

    if (!network) {
      return {
        isSuspicious: false,
        riskLevel: 0,
        reasons: [],
        botCount: 0,
        lastActivity: null
      };
    }

    const reasons: string[] = [];
    if (network.suspicionLevel > 0.5) {
      reasons.push(`High suspicion level (${Math.round(network.suspicionLevel * 100)}%)`);
    }
    
    if (botAccounts.length > 0) {
      reasons.push(`${botAccounts.length} bot accounts involved`);
    }
    
    if (network.campaignType === 'pump_and_dump') {
      reasons.push('Pump and dump campaign detected');
    }
    
    if (activeAlert) {
      reasons.push(`Active ${activeAlert.alertType} alert`);
    }

    return {
      isSuspicious: network.suspicionLevel > 0.5 || botAccounts.length > 0,
      riskLevel: network.suspicionLevel,
      reasons,
      botCount: botAccounts.length,
      lastActivity: network.peakActivity
    };
  }

  /**
   * Generate watchlist of tokens to monitor
   */
  generateWatchlist(): Array<{
    token: string;
    priority: 'high' | 'medium' | 'low';
    reason: string;
    action: string;
  }> {
    const watchlist: Array<{token: string; priority: 'high' | 'medium' | 'low'; reason: string; action: string}> = [];
    
    // Add critical alerts
    for (const alert of this.getCriticalAlerts()) {
      watchlist.push({
        token: alert.tokenSymbol,
        priority: 'high',
        reason: `${alert.alertType} - ${alert.severity}`,
        action: 'Immediate investigation required'
      });
    }

    // Add high-risk tokens
    for (const riskToken of this.getHighRiskTokens().slice(0, 5)) {
      if (!watchlist.some(w => w.token === riskToken.token)) {
        watchlist.push({
          token: riskToken.token,
          priority: 'medium',
          reason: `High risk level (${Math.round(riskToken.riskLevel * 100)}%)`,
          action: 'Monitor for dump signals'
        });
      }
    }

    // Add tokens with high bot activity
    const botStats = this.botTracker.getBotStatistics();
    for (const {token, count} of botStats.mostShilledTokens.slice(0, 3)) {
      if (!watchlist.some(w => w.token === token)) {
        watchlist.push({
          token,
          priority: 'low',
          reason: `${count} bot accounts shilling`,
          action: 'Track social sentiment'
        });
      }
    }

    return watchlist;
  }
}